import { sum } from "lodash";

import type { DemandPoint, LayerClassificationConfig, LayerClassificationResult, ParcelLayerItem, StreetLayerItem } from "./layer";

/**
 * A request to perform street parcel classification.
 */
export interface StreetParcelRequest {
  /** The parcels to classify. */
  parcels: ParcelLayerItem[];

  /** The streets to include in the classification. */
  streets: StreetLayerItem[];

  /** The demands to include in the classification. */
  demands?: DemandPoint[];

  /** Classification configuration. */
  config?: LayerClassificationConfig;

  /** The ID of the request. */
  requestId: number;
}

/** Messages the worker posts back. */
export type StreetParcelResponse =
  | { type: "progress"; completed: number; total: number; requestId: number }
  | { type: "result"; results: LayerClassificationResult[]; requestId: number };

export type ClassifyParcelLayerOptions = Omit<StreetParcelRequest, "requestId"> & {
  /** Optional progress tracking callback. */
  onProgress?: (completed: number, total: number) => void;
};

let nextRequestId = 1;

/**
 * Classify a shard of parcels on a single worker, resolving with one result per parcel in input
 * order and rejecting if the worker errors.
 *
 * Multiple requests can safely be in flight on the same worker at once; each call receives only its
 * own progress callbacks and results.
 */
function classifyShardInWorker(worker: Worker, options: ClassifyParcelLayerOptions): Promise<LayerClassificationResult[]> {
  const requestId = nextRequestId;
  nextRequestId += 1;
  return new Promise<LayerClassificationResult[]>((resolve, reject) => {
    const cleanup = (): void => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      worker.removeEventListener("messageerror", onError);
    };
    const onError = (): void => {
      cleanup();
      reject(new Error("Street parcel classification worker failed"));
    };
    const onMessage = (event: MessageEvent<StreetParcelResponse>): void => {
      const message = event.data;

      // message is for a different request; ignore
      if (message.requestId !== requestId) return;

      // push progress updates to caller
      if (message.type === "progress") {
        options.onProgress?.(message.completed, message.total);
        return;
      }

      // result achieved: clean up.
      cleanup();
      resolve(message.results);
    };
    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);
    worker.addEventListener("messageerror", onError);
    const request: StreetParcelRequest = {
      parcels: options.parcels,
      streets: options.streets,
      demands: options.demands,
      config: options.config,
      requestId,
    };
    worker.postMessage(request);
  });
}

/**
 * Classify a parcel layer in parallel across the given workers, resolving with one result per parcel
 * in input order. Throws if `workers` is empty.
 *
 * The caller owns the workers' lifetimes; prefer `classifyParcelLayerInWorkerPool` unless you
 * manage them yourself.
 */
export async function classifyParcelLayerWithWorkers(workers: Worker[], options: ClassifyParcelLayerOptions): Promise<LayerClassificationResult[]> {
  if (workers.length === 0) {
    throw new Error("classifyParcelLayerWithWorkers requires at least one worker");
  }

  const { parcels, streets, demands, config, onProgress } = options;
  const total = parcels.length;
  const shardSize = Math.ceil(total / workers.length);
  const completedPerWorker = new Array<number>(workers.length).fill(0);

  const shardResults = await Promise.all(
    workers.map((worker, i) => {
      const shard = parcels.slice(i * shardSize, (i + 1) * shardSize);
      if (shard.length === 0) return Promise.resolve<LayerClassificationResult[]>([]);
      return classifyShardInWorker(worker, {
        parcels: shard,
        streets,
        demands,
        config,
        onProgress: onProgress
          ? (completed) => {
              completedPerWorker[i] = completed;
              onProgress(sum(completedPerWorker), total);
            }
          : undefined,
      });
    })
  );

  return shardResults.flat();
}

/**
 * Classify a parcel layer in a self-managed worker pool, resolving with one result per parcel
 * in input order.
 *
 * Workers are created with `createWorker`, sized to the available cores, and always terminated
 * once the run settles. Aborting `signal` terminates the pool immediately and rejects with the
 * abort reason.
 */
export async function classifyParcelLayerInWorkerPool(
  createWorker: () => Worker,
  options: ClassifyParcelLayerOptions & { signal?: AbortSignal }
): Promise<LayerClassificationResult[]> {
  const { signal, ...classifyOptions } = options;
  signal?.throwIfAborted();

  const poolSize = Math.max(1, Math.min((navigator.hardwareConcurrency ?? 4) - 1, 8));
  const workers = Array.from({ length: poolSize }, createWorker);
  let onAbort: (() => void) | undefined;
  try {
    const classified = classifyParcelLayerWithWorkers(workers, classifyOptions);
    if (!signal) return await classified;

    const aborted = new Promise<never>((_, reject) => {
      onAbort = () => reject(signal.reason);
      signal.addEventListener("abort", onAbort, { once: true });
    });
    return await Promise.race([classified, aborted]);
  } finally {
    if (onAbort) signal?.removeEventListener("abort", onAbort);
    workers.forEach((worker) => worker.terminate());
  }
}
